import { AdministrationIcon } from '@shared/icons/AdministrationIcon';
import type { LinkProps } from '@tanstack/react-router';
import { Link, useLocation } from '@tanstack/react-router';
import { useMemo } from 'react';

import { LinkChild } from '@/components/molecules/LinkChild';
import { messages as SidebarMessages } from '@/components/organism/Sidebar/messages.ts';
import type { ITabItemRouted } from '@/components/organism/TabJustifyPills/model.ts';
import { useMessages } from '@/hooks/messages';
import { AdministrationTabMessages } from '@/pages/administration/messages.ts';
import { eAdministrationTab } from '@/pages/administration/model.tsx';

interface IAdministrationSidebarProps {
  linkProps: LinkProps & { className?: string };
  isSidebarOpen: boolean;
  onItemClick?: () => void;
}

const AdministrationSidebar: React.FC<IAdministrationSidebarProps> = ({
  linkProps,
  isSidebarOpen,
  onItemClick,
}) => {
  const sidebarMessages = useMessages(SidebarMessages);
  const messages = useMessages(AdministrationTabMessages);
  const location = useLocation();

  // EWM-1041: pure function of the current location — computed during render instead of
  // mirrored into state by an effect, which used to paint the section collapsed/inactive
  // for one commit after every navigation into /administration.
  const isActive = location.href.includes('/administration');

  const tabsConfiguration: ITabItemRouted[] = useMemo(
    () => [
      {
        tabId: eAdministrationTab.tenantSettings,
        label: messages.tenantSettingsLabel(),
        link: '/administration/tenant-settings',
      },
      {
        tabId: eAdministrationTab.individualisation,
        label: messages.individualisationLabel(),
        link: '/administration/individualisation',
      },
      {
        tabId: eAdministrationTab.projectTypes,
        label: messages.projectTypes(),
        link: '/administration/project-types',
      },
      {
        tabId: eAdministrationTab.estimationMethodologies,
        label: messages.estimationMethodologies(),
        link: '/administration/estimation-methodologies',
      },
      {
        tabId: eAdministrationTab.estimationModels,
        label: messages.estimationModels(),
        link: '/administration/estimation-models',
      },
      {
        tabId: eAdministrationTab.users,
        label: messages.users(),
        link: '/administration/users',
      },
      {
        tabId: eAdministrationTab.workflow,
        label: messages.workflow(),
        link: '/administration/workflow',
      },
      {
        tabId: eAdministrationTab.aiWizard,
        label: messages.aiWizard(),
        link: '/administration/ai-wizard',
      },
      {
        tabId: eAdministrationTab.exportSettings,
        label: messages.exportSettings(),
        link: '/administration/export-settings',
      },
      {
        tabId: eAdministrationTab.notifications,
        label: messages.notifications(),
        link: '/administration/notifications',
      },
      {
        tabId: eAdministrationTab.tipsNews,
        label: messages.tipsNews(),
        link: '/administration/tips-news',
      },
      {
        tabId: eAdministrationTab.usageAnalytics,
        label: messages.usageAnalytics(),
        link: '/administration/usage-analytics',
      },
    ],
    [messages],
  );

  const mappedAdministrationLinks = useMemo(
    () =>
      tabsConfiguration.map((tab) => {
        return (
          <LinkChild key={tab.tabId} link={tab.link} label={tab.label} onClick={onItemClick} />
        );
      }),
    [tabsConfiguration, onItemClick],
  );

  return (
    <div className={linkProps.className}>
      <Link to='/administration' activeOptions={{ exact: false }} onClick={onItemClick}>
        <div className='w-full'>
          <div className='flex gap-2'>
            <div className='h-6 w-6'>
              <AdministrationIcon className='text-c1' />
            </div>
            <span
              className={`font-semibold transition-opacity duration-200 ${isSidebarOpen ? 'opacity-100' : 'opacity-0'} whitespace-nowrap`}
            >
              {sidebarMessages.administration()}
            </span>
          </div>
        </div>
      </Link>
      <div
        className={`flex flex-col gap-2 ${
          isSidebarOpen ? 'max-h-[1500px]' : 'max-h-0'
        } overflow-hidden transition-[max-height] duration-300 ease-in-out`}
      >
        {isActive && <div className='mt-5 grid w-full'>{mappedAdministrationLinks}</div>}
      </div>
    </div>
  );
};

export default AdministrationSidebar;
